"use client";

import { ApolloProvider } from "@apollo/client";
import { GoogleTagManager } from "@next/third-parties/google";
import { APIProvider } from "@vis.gl/react-google-maps";
import { YzThemeProvider } from "@yardzen/components/theme-provider";
import {
  getSplitUserKey,
  getYzsaId,
  GQLClient,
  initializeSplitForGTM,
} from "@yardzen/next-client-util";
import { useSession } from "next-auth/react";
import { ReactNode, useEffect } from "react";
import { DatadogWithSession } from "../../components/DatadogWithSession";
import { PageViewTracker } from "../../components/PageViewTracker";
import { YzsaSync } from "../../components/YzsaSync";
import { ClientAuthChecker } from "./ClientAuthChecker";
import MainContent from "./MainContent";

const client = GQLClient();
const googleTagManagerId = process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID;

interface ClientsideLayoutProps {
  children: ReactNode;
}

function ClientsideLayoutInner({ children }: ClientsideLayoutProps) {
  const { data: session } = useSession();
  const userId = (session?.user as { id?: string } | undefined)?.id;

  // Preserve GTM reporting without evaluating every flag just for RUM labels.
  useEffect(() => {
    const splitClientKey = process.env.NEXT_PUBLIC_SPLIT_CLIENT_KEY;

    if (!splitClientKey) {
      console.warn("[Split GTM] NEXT_PUBLIC_SPLIT_CLIENT_KEY not configured");
      return;
    }

    const anonymousId = getYzsaId();
    const userKey = getSplitUserKey(userId, anonymousId);

    // Initialize Split client and push to GTM dataLayer
    initializeSplitForGTM(splitClientKey, userKey).catch((error) => {
      console.error("[Split GTM] Failed to initialize:", error);
    });
  }, [userId]);

  return (
    <>
      {googleTagManagerId && (
        <GoogleTagManager
          gtmId={googleTagManagerId}
          gtmScriptUrl="https://yardwork.yardzen.com/gtm.js"
        />
      )}

      <APIProvider
        apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!}
        onLoad={() => console.log("Google Maps API has loaded.")}
      >
        <YzsaSync />
        <DatadogWithSession service="build-marketplace" />
        <ClientAuthChecker />
        <PageViewTracker />
        <ApolloProvider client={client}>
          <YzThemeProvider>
            <MainContent>{children}</MainContent>
          </YzThemeProvider>
        </ApolloProvider>
      </APIProvider>
    </>
  );
}

// Note: SessionProvider is provided by SessionWrapper in the layout
export default function ClientsideLayout({ children }: ClientsideLayoutProps) {
  return <ClientsideLayoutInner>{children}</ClientsideLayoutInner>;
}
